<template>
  <div class="min-h-screen py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Header -->
      <div class="mb-8 flex justify-between items-end">
        <div>
           <h1 class="text-3xl font-bold text-slate-900 dark:text-white">Observability</h1>
           <p class="mt-2 text-slate-600 dark:text-slate-400">AI-Powered Anomaly Detection & Metric Monitoring (Snowflake Cortex).</p>
        </div>
        <select v-model="selectedTable" class="rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2">
            <option value="WEB_EVENTS">WEB_EVENTS</option>
            <option value="ORDERS">ORDERS</option>
            <option value="USERS">USERS</option>
        </select>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <!-- Main Chart Section -->
        <div class="lg:col-span-2 space-y-8">
            <div class="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 class="text-lg font-medium text-slate-900 dark:text-white mb-4">Volume History: {{ selectedTable }}</h3>
                <IntelligenceAnomalyChart :history="history" :anomalies="tableAnomalies" />
            </div>

            <!-- Stats Cards -->
            <div class="grid grid-cols-3 gap-4">
                 <div class="bg-white dark:bg-slate-800 p-4 rounded-lg shadow border-l-4 border-indigo-500">
                    <p class="text-xs font-medium text-slate-500 uppercase">Avg Row Count</p>
                    <p class="text-2xl font-bold text-slate-900 dark:text-white mt-1">{{ formatNumber(avgRows) }}</p>
                 </div>
                  <div class="bg-white dark:bg-slate-800 p-4 rounded-lg shadow border-l-4 border-pink-500">
                    <p class="text-xs font-medium text-slate-500 uppercase">Anomalies (7d)</p>
                    <p class="text-2xl font-bold text-slate-900 dark:text-white mt-1">{{ tableAnomalies.length }}</p>
                 </div>
                  <div class="bg-white dark:bg-slate-800 p-4 rounded-lg shadow border-l-4 border-emerald-500">
                    <p class="text-xs font-medium text-slate-500 uppercase">Freshness Score</p>
                    <p class="text-2xl font-bold text-slate-900 dark:text-white mt-1">98%</p>
                 </div>
            </div>
        </div>

        <!-- Sidebar Feed -->
        <div class="lg:col-span-1">
             <IntelligenceAlertFeed :alerts="allAnomalies" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const selectedTable = ref('WEB_EVENTS')
const history = ref<any[]>([])
const allAnomalies = ref<any[]>([])

// Fetch History for selected table
const fetchHistory = async () => {
    const { data } = await useFetch('/api/mocks/cortex/history', {
        method: 'POST',
        body: { tableName: selectedTable.value }
    })
    history.value = (data.value as any[]) || []
}

// Fetch Anomalies (global or table specific)
const fetchAnomalies = async () => {
    // In real app, we might filter by table on server, here we fetch all and filter client side for alerts
    const { data } = await useFetch('/api/mocks/cortex/anomalies')
    if (data.value) {
        allAnomalies.value = (data.value as any[]).filter(a => a.is_anomaly)
    }
}

watch(selectedTable, fetchHistory, { immediate: true })
fetchAnomalies()

const tableAnomalies = computed(() => {
    return allAnomalies.value.filter(a => a.table_name === selectedTable.value)
})

const avgRows = computed(() => {
    if (!history.value.length) return 0
    const sum = history.value.reduce((acc, curr) => acc + curr.row_count, 0)
    return Math.floor(sum / history.value.length)
})

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(num);
}
</script>
